import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import {
  FullCalendarComponent,
  FullCalendarModule,
} from '@fullcalendar/angular';
import { CalendarOptions } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import { format } from 'date-fns';
import { HttpService } from '../../services/http.service';
import { InputComponent } from '../from-input/input.component';
import { CustomToastComponent } from '../toast/custom-toast.component';
import { UrlParserService } from '../../services/url-parser.service';

@Component({
  selector: 'app-event-calendar',
  standalone: true,
  imports: [CommonModule, FullCalendarModule, FormsModule, InputComponent],
  templateUrl: './event-calendar.component.html',
  styleUrls: ['./event-calendar.component.scss'],
})
export class EventCalendarComponent implements OnInit {
  @ViewChild('calendar') calendarComponent!: FullCalendarComponent;
  @ViewChild('nativeDatePicker') nativeDatePicker!: ElementRef<HTMLInputElement>;

  calendarOptions!: CalendarOptions;
  currentEvents: any[] = [];
  showEventModal = false;
  selectedEvent: any = null;
  isLoading = false;

  selectedDate: string = '';
  currentView: string = 'timeGridWeek';
  private isInternalDateChange = false; // Flag to prevent circular updates

  constructor(
    private httpService: HttpService,
    private snackBar: MatSnackBar,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private urlParser: UrlParserService,
  ) {}

  ngOnInit(): void {
    const today = new Date();
    this.selectedDate = this.formatDateForInput(today);
    this.initializeCalendarOptions();
  }

  private initializeCalendarOptions(): void {
    this.calendarOptions = {
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
      initialView: this.currentView,
      weekends: false,
      headerToolbar: false,
      allDayText: '',
      selectable: true,
      allDaySlot: true,
      displayEventTime: false,
      initialDate: this.selectedDate,

      eventClick: this.handleEventClick.bind(this),
      datesSet: this.handleDatesSet.bind(this),
      events: this.fetchCalendarEvents.bind(this),
      eventContent: this.renderEventContent.bind(this),

      dayHeaderContent: (arg) => {
        const date = arg.date;
        const dayName = date.toLocaleDateString('en', { weekday: 'long' });
        const dayNumber = date.getDate();
        return {
          html: `<div><span>${dayName} </span><div class="title-header-date"><span>${dayNumber}</span></div></div>`,
        };
      },

      views: {
        timeGridWeek: {
          titleFormat: { year: 'numeric', month: 'long' },
        },
        dayGridMonth: {
          titleFormat: { year: 'numeric', month: 'long' },
        },
        timeGridDay: {
          titleFormat: { year: 'numeric', month: 'long', day: 'numeric' },
        },
      },
    };
  }

  handleDatesSet(dateInfo: any): void {
    // Only update selectedDate if this is not from a date picker change
    if (!this.isInternalDateChange) {
      const currentDate = dateInfo.view.currentStart;
      this.selectedDate = this.formatDateForInput(currentDate);
    }
    this.cdr.detectChanges();
  }

  formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  openDatePicker(): void {
    this.nativeDatePicker?.nativeElement?.showPicker?.();
    this.nativeDatePicker?.nativeElement?.click();
  }

  formatDisplayDate(dateString: string): string {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}-${month}-${year}`;
  }

  onNativeDateChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.onDateChange(input.value);
  }

  onDateChange(dateString: string): void {
    if (!dateString || this.isLoading) return;

    this.isInternalDateChange = true;
    this.selectedDate = dateString;

    const date = new Date(dateString);
    const calendarApi = this.calendarComponent?.getApi();

    if (calendarApi) {
      calendarApi.gotoDate(date);
      // Reset the flag after a short delay
      setTimeout(() => {
        this.isInternalDateChange = false;
      }, 200);
    }
  }

  navigateDate(direction: 'prev' | 'next'): void {
    if (this.isLoading) return;

    const calendarApi = this.calendarComponent?.getApi();
    if (calendarApi) {
      if (direction === 'prev') {
        calendarApi.prev();
      } else {
        calendarApi.next();
      }
    }
  }

  goToToday(): void {
    if (this.isLoading) return;

    const calendarApi = this.calendarComponent?.getApi();
    if (calendarApi) {
      calendarApi.today();
      // Update selected date
      this.selectedDate = this.formatDateForInput(new Date());
    }
  }

  changeView(view: string): void {
    if (this.isLoading) return;

    this.currentView = view;
    const calendarApi = this.calendarComponent?.getApi();

    if (calendarApi) {
      calendarApi.changeView(view);
    }
  }

  fetchCalendarEvents(
    fetchInfo: any,
    successCallback: any,
    failureCallback: any,
  ) {
    this.isLoading = true;

    const startDate = format(fetchInfo.start, 'yyyy-MM-dd');
    const endDate = format(fetchInfo.end, 'yyyy-MM-dd');

    const url = `events/calendars?startDate=${startDate}&endDate=${endDate}`;

    this.httpService.get<any>(url).subscribe({
      next: (res: any) => {
        const events = res.data.map((event: any) => {
          const status = event.eventStatus;
          const color = this.getStatusColor(status);

          // Parse dates properly
          const eventStartDate = new Date(event.eventStartDate);
          const eventEndDate = new Date(event.eventEndDate);

          // Format dates for FullCalendar
          const startStr = this.formatDateForFullCalendar(eventStartDate);

          // For end date, we need to add one day for all-day events
          const endDateObj = new Date(eventEndDate);
          endDateObj.setDate(endDateObj.getDate() + 1);
          const endStr = this.formatDateForFullCalendar(endDateObj);

          return {
            id: event.eventId,
            title: event.eventName,
            start: startStr,
            end: endStr,
            startDate: event.eventStartDate,
            endDate: event.eventEndDate,
            allDay: true,
            backgroundColor: this.hexToRgba(color, 0.15),
            borderColor: color,
            textColor: '#ffffff',
            extendedProps: {
              eventTime: event.eventTime,
              location: event.location,
              status: status,
              description: event.description,
              segment: event.segment,
              startDate: event.eventStartDate,
              endDate: event.eventEndDate,
            },
          };
        });

        successCallback(events);
      },
      error: (err) => {
        console.error('Failed to load calendar events:', err);
        this.showErrorToast(err?.message || 'Failed to load events');
        failureCallback(err);
      },
      complete: () => {
        this.isLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  formatDateForFullCalendar(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private getStatusColor(status: string): string {
    const colors: { [key: string]: string } = {
      draft: '#2cc3e8',
      Ongoing: '#b45f9e',
      Upcoming: '#ffdd2e',
      'Already Held': '#50b748',
      Completed: '#50b748',
      'On Hold': '#faa633',
      'Event Cancelled': '#f04e30',
      Cancelled: '#f04e30',
    };
    return colors[status] || '#3788d8';
  }

  hexToRgba(hex: string, alpha: number = 1): string {
    hex = hex.replace('#', '');

    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((c) => c + c)
        .join('');
    }

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  handleEventClick(clickInfo: any): void {
    const event = clickInfo.event;

    this.selectedEvent = {
      id: event.id,
      title: event.title,
      start: event.start,
      end: event.end || event.start,
      startDate: event.extendedProps?.['startDate'],
      endDate: event.extendedProps?.['endDate'],
      status: this.getDisplayStatus(event.extendedProps?.['status']),
      location: event.extendedProps?.['location'],
      eventTime: event.extendedProps?.['eventTime'],
      color: event.backgroundColor,
      borderColor: event.borderColor,
      description:
        event.extendedProps?.['description'] || 'No description added',
      segment: event.extendedProps?.['segment'] || 'admin',
    };

    this.showEventModal = true;
  }

  closeEventModal(): void {
    this.showEventModal = false;
    this.selectedEvent = null;
  }

  viewEvent(): void {
    if (this.selectedEvent?.id) {
      const encodedId = this.urlParser.encodeId(String(this.selectedEvent.id));
      const encodedSegment = this.urlParser.encodeId(
        this.selectedEvent.segment || 'admin',
      );
      this.router.navigate(['/events/event-list/view', encodedId], {
        queryParams: { segment: encodedSegment },
      });
    }
  }

  formatDate(date: string | Date | null): string {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    const localDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    return localDate.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  renderEventContent(eventInfo: any): any {
    const { title } = eventInfo.event;
    const { eventTime, location, status } = eventInfo.event.extendedProps;
    const color = eventInfo.event.borderColor;

    return {
      html: `
      <div class="fc-event-main-frame" title="${title}">
        <div class="fc-event-frame-container">
          <div class="fc-event-title-container">
            <h2 style="color:${color}">${title}</h2>
            ${
              eventTime
                ? `
            <h3>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/>
                <path d="M12 7v5l3 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>${eventTime}</span>
            </h3>`
                : ''
            }
            ${
              location
                ? `
            <h4>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22s7-6.5 7-12a7 7 0 1 0-14 0c0 5.5 7 12 7 12z" stroke="currentColor" stroke-width="2"/>
                <circle cx="12" cy="10" r="2.5" stroke="currentColor" stroke-width="2"/>
              </svg>
              <span>${location}</span>
            </h4>`
                : ''
            }
          </div>
        </div>
        <button style="box-shadow: 0px 0px 10px 2px ${this.hexToRgba(color, 0.25)} inset; background:${this.hexToRgba(color, 0.25)}; border:1px solid ${this.hexToRgba(color, 0.75)}; color:${color}">
          ${this.getDisplayStatus(status)}
        </button>
      </div>`,
    };
  }

  getDisplayStatus(status: string): string {
    if (!status) return '';
    const s = status.toLowerCase();
    if (s === 'ongoing') return 'On Today';
    if (s === 'event cancelled') return 'Cancelled';
    return status;
  }

  private showErrorToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'error-toast'],
    });
  }
}
